export function initMenuOverlay(loco) {
  const body = document.body;
  const overlay = document.querySelector(".menu-overlay");
  const triggers = document.querySelectorAll("[data-menu-overlay-trigger]");
  if (!overlay || !triggers.length || !body) return;
  const gsap = window.gsap;
  const lenis = loco?.lenisInstance;
  const backdrop = overlay.querySelector(".menu-overlay__backdrop");
  const scrollContent = overlay.querySelector(".menu-overlay__content");
  const sygnetPath = overlay.querySelector(".brand-sygnet__path");

  const pauseLenis = () => {
    if (typeof lenis?.stop === "function") lenis.stop();
  };

  const resumeLenis = () => {
    if (typeof lenis?.start === "function") lenis.start();
  };

  const mainItems = overlay.querySelectorAll(".menu-overlay__main [data-overlay-animate]");
  const sideItems = overlay.querySelectorAll(".menu-overlay__side [data-overlay-animate]");
  const animatedItems = [...mainItems, ...sideItems];
  const mobileMenuMq = window.matchMedia("(max-width: 991.98px)");
  const isMobileMenu = () => mobileMenuMq.matches;
  let activeTl = null;

  const ensureSygnetClip = () => {
    if (!sygnetPath) return;
    const svg = sygnetPath.ownerSVGElement;
    if (!svg || svg.querySelector("#menu-overlay-sygnet-clip")) return;

    const NS = "http://www.w3.org/2000/svg";
    const defs = document.createElementNS(NS, "defs");
    const clip = document.createElementNS(NS, "clipPath");
    clip.setAttribute("id", "menu-overlay-sygnet-clip");
    const clipPath = document.createElementNS(NS, "path");
    clipPath.setAttribute("d", sygnetPath.getAttribute("d") || "");
    clip.appendChild(clipPath);
    defs.appendChild(clip);
    svg.insertBefore(defs, svg.firstChild);
    sygnetPath.setAttribute("clip-path", "url(#menu-overlay-sygnet-clip)");
  };

  const prepareSygnetStroke = () => {
    if (!gsap || !sygnetPath) return;

    ensureSygnetClip();

    const length = sygnetPath.getTotalLength();
    const ctm = sygnetPath.getScreenCTM();
    const scale = ctm ? Math.hypot(ctm.a, ctm.b) : 1;
    // ~3.5px stroke clipped to fill → ~1.5–2px inner line on screen
    const strokeUser = 3.5 / (scale || 1);
    const dash = length + strokeUser * 2;

    sygnetPath.setAttribute("stroke-width", String(strokeUser));
    gsap.set(sygnetPath, {
      attr: {
        "stroke-dasharray": dash,
        "stroke-dashoffset": dash,
      },
    });
    sygnetPath.dataset.strokeLength = String(dash);
  };

  const hiddenState = () => (isMobileMenu() ? { opacity: 0 } : { opacity: 0, y: 100 });

  const visibleState = () => (isMobileMenu() ? { opacity: 1 } : { opacity: 1, y: 0 });

  const setExpanded = (expanded) => {
    triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  };

  const resetItems = () => {
    if (!gsap) return;
    gsap.set(animatedItems, hiddenState());
    if (backdrop) {
      gsap.set(backdrop, { scaleY: 0, transformOrigin: "top center" });
    }
    prepareSygnetStroke();
  };

  const animateItemsIn = () => {
    if (!gsap) return;
    if (activeTl) activeTl.kill();
    const tl = gsap.timeline();
    activeTl = tl;

    if (backdrop) {
      tl.to(backdrop, {
        scaleY: 1,
        duration: 0.9,
        ease: "expo.out",
      });
    }

    if (sygnetPath) {
      tl.to(
        sygnetPath,
        {
          attr: { "stroke-dashoffset": 0 },
          duration: 1.8,
          ease: "power2.inOut",
        },
        "-=0.55",
      );
    }

    if (isMobileMenu()) {
      tl.to(
        mainItems,
        {
          ...visibleState(),
          duration: 0.55,
          stagger: 0.07,
          ease: "power2.out",
          clearProps: "transform",
        },
        "-=1.15",
      ).to(
        sideItems,
        {
          ...visibleState(),
          duration: 0.5,
          stagger: 0.06,
          ease: "power2.out",
          clearProps: "transform",
        },
        "-=0.2",
      );
      return;
    }

    tl.to(
      mainItems,
      {
        ...visibleState(),
        duration: 1.2,
        stagger: 0.11,
        ease: "expo.out",
      },
      "-=1.25",
    ).to(
      sideItems,
      {
        ...visibleState(),
        duration: 1.1,
        stagger: 0.09,
        ease: "expo.out",
      },
      "<0.28",
    );
  };

  const animateItemsOut = () => {
    if (!gsap) return null;
    if (activeTl) activeTl.kill();
    const tl = gsap.timeline({
      onComplete: () => {
        body.classList.remove("menu-overlay-open");
        overlay.setAttribute("aria-hidden", "true");
        setExpanded(false);
        resetItems();
        resumeLenis();
      },
    });
    activeTl = tl;

    tl.to(animatedItems, {
      opacity: 0,
      ...(isMobileMenu() ? {} : { y: -8 }),
      duration: 0.34,
      stagger: { each: 0.025, from: "end" },
      ease: "power2.inOut",
      clearProps: isMobileMenu() ? "transform" : undefined,
    });

    if (sygnetPath) {
      tl.to(
        sygnetPath,
        {
          attr: {
            "stroke-dashoffset": Number(sygnetPath.dataset.strokeLength) || 0,
          },
          duration: 0.35,
          ease: "power2.in",
        },
        "<",
      );
    }

    if (backdrop) {
      tl.to(
        backdrop,
        {
          scaleY: 0,
          duration: 0.42,
          ease: "expo.in",
        },
        "-=0.12",
      );
    }

    return tl;
  };

  prepareSygnetStroke();
  resetItems();

  const open = () => {
    if (scrollContent) scrollContent.scrollTop = 0;
    pauseLenis();
    body.classList.add("menu-overlay-open");
    overlay.setAttribute("aria-hidden", "false");
    setExpanded(true);

    // Measure after overlay is visible so CTM / dash length match the 80% scale.
    requestAnimationFrame(() => {
      resetItems();
      prepareSygnetStroke();
      animateItemsIn();
    });
  };

  const close = () => {
    if (!gsap) {
      body.classList.remove("menu-overlay-open");
      overlay.setAttribute("aria-hidden", "true");
      setExpanded(false);
      resetItems();
      resumeLenis();
      return;
    }
    animateItemsOut();
  };

  const toggle = () => {
    if (body.classList.contains("menu-overlay-open")) {
      close();
      return;
    }
    open();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", toggle);
  });

  overlay.querySelectorAll("[data-overlay-close]").forEach((el) => {
    el.addEventListener("click", close);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  scrollContent?.addEventListener(
    "wheel",
    (event) => {
      event.stopPropagation();
    },
    { passive: true },
  );

  scrollContent?.addEventListener(
    "touchmove",
    (event) => {
      event.stopPropagation();
    },
    { passive: true },
  );
}
